/**
 * The customer-facing shipment URL the portal hands out:
 *
 *     https://track.westernalliancelogistics.com/?walId=WAL-XXXX-123
 *
 * THE PATH DEPENDS ON WHICH ORIGIN IS CONFIGURED. The dedicated tracking host serves the lookup at
 * its ROOT — routes.tsx gives that hostname the search page at / and nothing else — so no path
 * segment belongs in the URL. Only when PUBLIC_TRACKING_URL is unset and this falls back to the
 * portal's own origin is the page one route among many and needs /public-tracking.
 *
 * It replaces the opaque token links (/share-links/radar-order?t=…, and /share-links/order?t=… for
 * a booked order) wherever a tracking URL is produced. A customer can read this one, repeat it over
 * the phone and type it into the tracking page; a token can do none of that. The portal backend and
 * WALHQ build the identical URL from their own copies of this rule (wal-customer-portal-be
 * src/utils/publicTrackingLink.ts, wal-backend-v2 api/utilities/public-tracking-link.utils.js), so
 * whichever product a link is copied from, it is the same link.
 *
 * The token routes stay live and keep being served — links already sent or saved must keep working.
 * Only newly produced URLs change.
 */
import { ORDER_STATUS } from 'src/constants/common';

import { getPortalOrigin } from './portalOrigin';

/** The route the PORTAL hostname serves the page on; kept here so this module has no import cycle with routes. */
const PORTAL_TRACKING_PATH = '/public-tracking';

/** The dedicated tracking hostname serves it at the root instead. */
const DEDICATED_TRACKING_PATH = '/';

/**
 * Statuses the public lookup answers for. MIRRORS wal-customer-portal-be src/utils/publicTracking.ts
 * (LIVE_STATUSES + POST_DELIVERY_STATUSES) and wal-backend-v2 api/utilities/public-tracking-link.utils.js
 * — all three must agree, or the portal hands out links that land on "We couldn't find that shipment".
 *
 * The billing statuses were once left out here, on the grounds that a post-delivery order never
 * reached the share modal. That stopped being true when Share Tracking Link began staying up through
 * the link's one-week post-delivery window (client, 2026-10-08): orders leave `delivered` for a
 * billing status within hours, so without them the modal would show "link not available" for most of
 * the window it is now deliberately open for.
 */
const PUBLIC_TRACKING_STATUSES: string[] = [
    ORDER_STATUS.BOOKED,
    ORDER_STATUS.DISPATCHED,
    ORDER_STATUS.IN_TRANSIT,
    ORDER_STATUS.DELIVERED,
    ORDER_STATUS.COMPLETED,
    ORDER_STATUS.INVOICE_CREATED,
    ORDER_STATUS.INVOICE_SENT,
    ORDER_STATUS.INVOICE_PAID,
];

/** Whether the public lookup will answer for this order status. */
export const isPublicTrackingStatus = (orderStatus: string | null | undefined): boolean => PUBLIC_TRACKING_STATUSES.includes(orderStatus as string);

/**
 * Origin + path for the public tracking page, as one decision — they cannot be chosen separately
 * without producing a URL that resolves to the wrong page.
 *
 * PUBLIC_TRACKING_URL is baked into index.html at build time; unset (local dev, an environment not
 * yet updated, or the %placeholder% left in place) falls back to the portal's own origin, which also
 * serves the page at /public-tracking — so this is never a broken link, only a less branded one.
 */
export const getPublicTrackingBase = (): { origin: string; path: string } => {
    const configured = typeof window.PUBLIC_TRACKING_URL === 'string' ? window.PUBLIC_TRACKING_URL.trim().replace(/\/+$/, '') : '';

    return /^https?:\/\//.test(configured) ? { origin: configured, path: DEDICATED_TRACKING_PATH } : { origin: getPortalOrigin(), path: PORTAL_TRACKING_PATH };
};

/** Origin only — kept for callers that just need to know where the page lives. */
export const getPublicTrackingOrigin = (): string => getPublicTrackingBase().origin;

/**
 * The public tracking URL for a WAL ID, or null when one cannot be built.
 *
 * Returns null rather than a half-formed URL on a missing WAL ID: callers have a token link to fall
 * back to, and a link that 404s is worse than no link at all.
 */
export const buildPublicTrackingUrl = (walId: string | null | undefined): string | null => {
    const id = String(walId ?? '').trim();
    if (!id) return null;

    const { origin, path } = getPublicTrackingBase();

    return `${origin}${path}?walId=${encodeURIComponent(id)}`;
};
